import { Markdown } from '@bloo-team/ui'
import { QueryHighlight, SqlEditor, useSearchFields } from '@bloo-team/core-shell'
import {
  readCodeSpec, readQuerySpec, readTextSpec, writeCodeSpec, writeQuerySpec, writeTextSpec,
} from '@bloo-team/api-client'
import type { BlockEditorProps, BlockViewProps } from './types'

/** Rows for a textarea: fits the text, 3..14. */
const rowsFor = (text: string) => Math.min(14, Math.max(3, text.split('\n').length + 1))

const runKey = (onRun: () => void) => (e: React.KeyboardEvent) => {
  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
    e.preventDefault()
    onRun()
  }
}

/* ------------------------------------------------------------ DQL / SQL */

export function QueryView({ block }: BlockViewProps) {
  const { query } = readQuerySpec(block)
  // Tier 2: the block's data — the query, highlighted, in an inset code well.
  return query
    ? <QueryHighlight className="wb-well" value={query} dialect={block.type === 'sql' ? 'sql' : 'dql'} />
    : <p className="hud-unit">No query yet.</p>
}

export function QueryEditor({ block, clusterId, scopeId, index, onChange, onRun }: BlockEditorProps) {
  const { query } = readQuerySpec(block)
  const vocab = useSearchFields({ clusterId, scopeId })
  const sql = block.type === 'sql'
  // The Search page's editor: highlight and field completion; ⌘↵ runs. Lint is paused
  // in workbooks — the DQL linter flags valid queries (a leading NOT, `as` aliases).
  return (
    <SqlEditor
      lint={false}
      value={query}
      onChange={(v) => onChange(writeQuerySpec(block, { query: v }))}
      onRun={onRun}
      dialect={sql ? 'sql' : 'dql'}
      vocabulary={vocab.data ?? null}
      ariaLabel={`Block ${index + 1} ${sql ? 'SQL' : 'DQL'} query`}
    />
  )
}

/* ------------------------------------------------------------ Text */

export function TextView({ block }: BlockViewProps) {
  const { text } = readTextSpec(block)
  // Authored notes: markdown, links allowed.
  return text.trim() ? <Markdown text={text} links /> : <p className="hud-unit">Empty note.</p>
}

export function TextEditor({ block, index, onChange }: BlockEditorProps) {
  const { text } = readTextSpec(block)
  return (
    <textarea
      className="hunt-editor"
      aria-label={`Block ${index + 1} text`}
      value={text}
      rows={rowsFor(text)}
      onChange={(e) => onChange(writeTextSpec(block, { text: e.target.value }))}
    />
  )
}


/* ------------------------------------------------------------ Code (pythonblock) */

/* Language and encoding are not shown: one language exists (python-3.8) and the
   base64 encoding is a wire detail. writeCodeSpec keeps both on save. */

export function CodeView({ block }: BlockViewProps) {
  const spec = readCodeSpec(block)
  return spec.source ? <pre className="wb-well">{spec.source}</pre> : <p className="hud-unit">No code yet.</p>
}

export function CodeEditor({ block, index, onChange, onRun }: BlockEditorProps) {
  const spec = readCodeSpec(block)
  return (
    <>
      <textarea
        className="hunt-editor mono"
        aria-label={`Block ${index + 1} python source`}
        value={spec.source}
        rows={rowsFor(spec.source)}
        spellCheck={false}
        placeholder={'def transform(inward_array):\n    return inward_array'}
        onChange={(e) => onChange(writeCodeSpec(block, { ...spec, source: e.target.value }))}
        onKeyDown={runKey(onRun)}
      />
      <span className="bui-hint">Transforms the previous block’s rows · ⌘↵ runs this block</span>
    </>
  )
}
